/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, inject, signal, effect } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { DesyAngularModule } from 'desy-angular';
import { SearchService } from '@/app/services/ia-services/search.service';
import { ProjectsService } from '@/app/services/ia-services/projects.service';
import { PillComponent } from '@/app/shared/pill/pill.component';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { Location } from '@angular/common';
import { ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { getError } from '@/app/utils/form-errors';
import { requiredNonEmptyArray } from '@/app/utils/required-non-empty-array.validator';
import { integerOnlyValidator } from '@/app/utils/integer-validation';
import { groupAdministrationFormatValidator } from '@/app/utils/group-administration-format.validator';

type RatSearchOption = {
  id_rat: number;
  nombre_rat: string;
  label: string;
};

type EmployeeRowValue = {
  totalPeople: unknown;
  groupAdministration: string;
  levelPosition: unknown;
};

@Component({
  selector: 'app-new-project',
  templateUrl: './new-project.component.html',
  styleUrls: ['./new-project.component.scss'],
  imports: [
    ConfirmDialog,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AutoCompleteModule,
    ButtonsComponent,
    DesyAngularModule,
    PillComponent,
    DatePickerModule,
    SelectButtonModule,
    MultiSelectModule,
  ],
  providers: [ConfirmationService],
})
export class NewProjectComponent {
  private projectsService = inject(ProjectsService);
  form: FormGroup;
  getError = getError;

  // currentDateFormatted sirve para mostrar la fecha actual en la explicación de Fecha Evaluación de Impacto
  get currentDateFormatted(): string {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date());
  }
  maxSelectableDate = this.getYesterdayDate();
  typeDataPersonalLabels: string[] = [];
  categoryDataPersonalLabels: string[] = [];
  typeDataPersonal = [
    { label: 'No trata datos personales', value: 2 },
    { label: 'Sí trata datos personales', value: 1 },
    { label: 'Anonimizados previamente', value: 3 },
    { label: 'Parcialmente seudonimizado', value: 4 },
    { label: 'Totalmente seudonimizados', value: 5 },
  ];
  categoryDataPersonal = [
    { label: 'No especial protección', value: '1' },
    { label: 'Datos de salud', value: '2' },
    { label: 'Origen étnico o racial', value: '3' },
    { label: 'Opiniones políticas, religiosas, sindicales', value: '4' },
    { label: 'Datos genéticos o biométricos', value: '5' },
    { label: 'Vida sexual u orientación', value: '6' },
  ];
  riskLevelOptions = [
    { label: 'Inaceptable', value: 'inaceptable' },
    { label: 'Alto', value: 'alto' },
    { label: 'Limitado', value: 'limitado' },
    { label: 'Mínimo', value: 'minimo' },
  ];
  typeDataPersonalOptions = this.typeDataPersonal.map((item) => item.label);
  categoryDataPersonalOptions = this.categoryDataPersonal.map((item) => item.label);
  organismsList = signal<{ organismo: string; codigo_organismo: string }[]>([]);

  usersList: string[] = [];
  proceduresList: string[] = [];
  procedureSearchLoading = false;
  ratList: RatSearchOption[] = [];
  procedureSearch: string | null = null;
  ratSearch: RatSearchOption | string | null = null;
  currentProcedures = signal<{ code: number; name: string }[]>([]);
  applications = signal<string[]>([]);
  application = signal<string>('');
  searchService = inject(SearchService);
  userLogged = JSON.parse(localStorage.getItem('userData') || '{}');
  fullName =
    this.userLogged.rpt +
    ' ' +
    '-' +
    ' ' +
    this.userLogged.givenName +
    ' ' +
    this.userLogged.surname1 +
    ' ' +
    this.userLogged.surname2 +
    ' ' +
    '-' +
    ' ' +
    this.userLogged.stationWork;
  formSubmitted = signal(false);

  private toNumber(value: unknown): number {
    return typeof value === 'number' ? value : Number(value);
  }

  private optionalEconomicAmount(value: unknown): number | null {
    return value === null || value === undefined || value === '' ? null : Number(value);
  }

  private getYesterdayDate(): Date {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    return yesterday;
  }

  private createEmployeeRow(): FormGroup {
    return this.fb.group({
      totalPeople: [null, [Validators.required, Validators.min(0)]],
      groupAdministration: [
        null,
        [Validators.required, Validators.maxLength(200), groupAdministrationFormatValidator()],
      ],
      levelPosition: [null, [Validators.required, Validators.min(0), integerOnlyValidator()]],
    });
  }

  get before(): FormArray {
    return this.form.get('before') as FormArray;
  }

  get after(): FormArray {
    return this.form.get('after') as FormArray;
  }

  get beforeEmployeeRows(): FormGroup[] {
    return this.before.controls as FormGroup[];
  }

  get afterEmployeeRows(): FormGroup[] {
    return this.after.controls as FormGroup[];
  }

  constructor(
    private fb: FormBuilder,
    private location: Location,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {
    this.form = this.fb.group({
      administrativeProcess: [null, Validators.maxLength(500)],
      biometricData: ['no'],
      categoryDataPersonalSelected: [['1']],
      contactEmailResponsible: [this.userLogged.email, [Validators.required, Validators.email]],

      before: this.fb.array([this.createEmployeeRow()]),
      after: this.fb.array([this.createEmployeeRow()]),
      amountAffectedCitizens: [null, [Validators.required, Validators.min(0), integerOnlyValidator()]],
      amountAffectedOfficials: [null, [Validators.required, Validators.min(0), integerOnlyValidator()]],
      economicEstimateAnnualMaintenance: [null, [Validators.min(0)]],
      economicEstimateConsumption: [null, [Validators.min(0)]],
      currentApplicationCostEstimate: [null, [Validators.required, Validators.min(0)]],
      currentStaffCostEstimate: [null, [Validators.required, Validators.min(0)]],
      postDeploymentStaffCostEstimate: [null, [Validators.required, Validators.min(0)]],
      averageResolutionEstimateTime: [null, [Validators.required, Validators.min(0)]],
      averageResolutionRealTime: [null, [Validators.required, Validators.min(0)]],
      otherDerivedCosts: [null, [Validators.min(0)]],
      economicEstimateImplementation: [null, [Validators.min(0)]],

      eipd: [''],
      csvEipd: [null],
      isDataSensitive: ['no-realizada'],
      projectName: ['', [Validators.required, Validators.pattern(/^[^:#]*$/)]],
      rat: ['', [Validators.required, Validators.pattern(/^[^:#]*$/)]],
      selectedProcedure: [[], [requiredNonEmptyArray]],
      systemFinality: ['', Validators.required],
      systemOrApp: [[], [requiredNonEmptyArray]],
      personalSelectedDataType: [0, Validators.required],
      userNameResponsible: [this.fullName, [Validators.required, Validators.pattern(/^[^:#]*$/)]],
      nif: [this.userLogged.nif, Validators.required],
      jobcode: [this.userLogged.roleCode, Validators.required],
      department: [{ value: this.userLogged.department, disabled: true }, Validators.required],
      orgCode: [this.userLogged.orgCode],
      selectedOrgCode: [{ value: this.userLogged.orgCode, disabled: true }, Validators.required],
      projectClassification: ['', Validators.required],
      serviceName: [{ value: this.userLogged.serviceName, disabled: true }],
      generalDirectorateCode: [this.userLogged.generalDirectorateCode],
    });

    effect(() => {
      const user = this.form.get('userNameResponsible')?.value;

      if (!user || typeof user !== 'object') {
        this.organismsList.set([
          {
            codigo_organismo: this.userLogged.orgCode,
            organismo: this.userLogged.orgName,
          },
        ]);
        return;
      }
      this.organismsList.set([
        {
          codigo_organismo: user.orgCode,
          organismo: user.organization,
        },
      ]);
    });
    effect(() => {
      const list = this.organismsList();
      if (!list.length) return;

      const formControl = this.form.get('selectedOrgCode');
      if (!formControl) return;

      const current = formControl.value;
      const match = list.find((o) => o.codigo_organismo === current);

      if (match) {
        formControl.setValue(current, { emitEvent: false });
      }
    });

    this.form.get('personalSelectedDataType')?.valueChanges.subscribe((typeValue) => {
      const categoryControl = this.form.get('categoryDataPersonalSelected');

      if (typeValue === 1) {
        categoryControl?.setValue([]);
        categoryControl?.setValidators([Validators.required]);
      } else {
        categoryControl?.clearValidators();
        categoryControl?.setValue(['1']);
      }

      categoryControl?.updateValueAndValidity();
    });

    this.form.get('userNameResponsible')?.valueChanges.subscribe((user) => {
      const nifControl = this.form.get('nif');
      const jobCodeControl = this.form.get('jobcode');
      const orgCodeControl = this.form.get('selectedOrgCode');
      const departmentControl = this.form.get('department');
      const serviceNameControl = this.form.get('serviceName');

      if (!user || typeof user !== 'object') {
        nifControl?.setValue(this.userLogged.nif);
        jobCodeControl?.setValue(this.userLogged.roleCode);
        orgCodeControl?.setValue(this.userLogged.orgCode);
        departmentControl?.setValue(this.userLogged.department);
        serviceNameControl?.setValue(this.userLogged.serviceName);
        return;
      }
      nifControl?.setValue(user.nif);
      jobCodeControl?.setValue(user.jobcode);
      orgCodeControl?.setValue(user.orgCode);
      departmentControl?.setValue(user.department);
      serviceNameControl?.setValue(user.servicio);
    });

    this.form.get('categoryDataPersonalSelected')?.valueChanges.subscribe((categories) => {
      const bioControl = this.form.get('biometricData');

      if (Array.isArray(categories) && categories.includes('5')) {
        bioControl?.setValidators([Validators.required]);
        bioControl?.setValue('');
      } else {
        bioControl?.clearValidators();
        bioControl?.setValue('no');
      }

      bioControl?.updateValueAndValidity();
    });

    this.form.get('isDataSensitive')?.valueChanges.subscribe((value) => {
      this.toggleEffectiveDateValidators(value);
    });
    this.toggleEffectiveDateValidators(this.form.get('isDataSensitive')?.value);

    const initialUserName = this.form.get('userNameResponsible')?.value;
    if (!initialUserName || initialUserName.trim() === '') {
      this.form.get('selectedOrgCode')?.disable();
    }
  }
  onTypeDataPersonalChange(label: string) {
    const selected = this.typeDataPersonal.find((item) => item.label === label);
    this.form.get('personalSelectedDataType')?.setValue(selected?.value || null);
  }

  getSelectedCategoriesText(values: any[]): string {
    if (!values) return '';
    return values.length > 2 ? `${values.length} opciones seleccionadas` : values.map((v) => v.label).join(', ');
  }

  getEmployeeRowError(row: FormGroup, controlName: string): string | null {
    return getError(row, controlName, this.formSubmitted());
  }

  addBeforeRow() {
    this.before.push(this.createEmployeeRow());
    this.before.markAsDirty();
    this.before.updateValueAndValidity();
  }

  removeBeforeRow(index: number) {
    if (this.before.length <= 1) return;

    this.before.removeAt(index);
    this.before.markAsDirty();
    this.before.updateValueAndValidity();
  }

  addAfterRow() {
    this.after.push(this.createEmployeeRow());
    this.after.markAsDirty();
    this.after.updateValueAndValidity();
  }

  removeAfterRow(index: number) {
    if (this.after.length <= 1) return;

    this.after.removeAt(index);
    this.after.markAsDirty();
    this.after.updateValueAndValidity();
  }

  private toggleEffectiveDateValidators(value: string) {
    const eff = this.form.get('eipd');
    const csvEipd = this.form.get('csvEipd');
    if (!eff || !csvEipd) return;

    csvEipd.clearValidators();

    if (value === 'no-realizada') {
      eff.clearValidators();
      eff.setValue('');
      csvEipd.setValue(null);
      eff.updateValueAndValidity();
    } else if (value === 'seleccionar-fecha') {
      csvEipd.setValidators([Validators.required]);
      eff.setValidators([
        Validators.required,
        (control) => {
          const value = control.value;
          if (!value) return null;
          const selectedDate = new Date(value);
          const today = new Date();
          selectedDate.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          return selectedDate < today ? null : { dateInFuture: true };
        },
      ]);
      eff.updateValueAndValidity();
    }

    csvEipd.updateValueAndValidity();
  }
  async onSearchOrganization(event: any) {
    const response: any = await this.searchService.search({ search: event.query, busqueda: 'organismos' });
    this.organismsList.set(response.data || []);
  }

  onOrganismSelected(event: any) {
    this.form.get('serviceName')?.enable();
    this.form.get('department')?.enable();
    this.form.patchValue({
      orgCode: event.value.codigo_organismo,
      department: event.value.departamento,
      serviceName: event.value.serviceName,
    });
    this.form.get('serviceName')?.disable();
    this.form.get('department')?.disable();
  }

  async onSearchUser(event: any) {
    const response: any = await this.searchService.search({ search: event.query, busqueda: 'personas' });
    this.usersList = response.data.map((user: any) => ({
      infoCompleted: `${user.rpt} - ${user.name} - ${user.workstation}`,
      nif: user.nif,
      orgCode: user.orgcode,
      jobcode: user.jobcode,
      department: user.department,
      organization: user.organization,
      serviceName: user.servicio,
      email: user.email || 'Introduzca un email válido',
      generalDirectorateCode: user.codigo_direccion_general,
    }));
    this.form.patchValue({
      contactEmailResponsible: event.value?.email,
    });
  }

  onUserSelected(event: any) {
    const user: any = event.value;
    this.form.patchValue({
      userNameResponsible: user.infoCompleted,
      nif: user.nif,
      jobcode: user.jobcode,
      department: user.department,
      selectedOrgCode: user.orgCode,
      serviceName: user.serviceName,
      contactEmailResponsible: user.email,
      generalDirectorateCode: user.generalDirectorateCode,
    });
    this.organismsList.set([
      {
        codigo_organismo: user.orgCode,
        organismo: user.organization,
      },
    ]);

    this.organismsList.set([...this.organismsList()]);
  }

  async onSearchProcedure(event: any) {
    this.procedureSearchLoading = true;
    this.proceduresList = [];
    try {
      const response: any = await this.searchService.search({ search: event.query, busqueda: 'procedimientos' });
      this.proceduresList = response.data || [];
    } finally {
      this.procedureSearchLoading = false;
    }
  }

  searchCurrentProcedure(autocomplete: AutoComplete, event?: Event) {
    const query = (this.procedureSearch ?? '').trim();
    if (query.length < 3) return;

    autocomplete.search(event, query, 'input');
  }

  onProcedureSearchEnter(event: Event, autocomplete: AutoComplete) {
    const activeOption = (event.target as HTMLElement | null)?.getAttribute('aria-activedescendant');
    if (activeOption) return;

    event.preventDefault();
    this.searchCurrentProcedure(autocomplete, event);
  }

  async onSearchRat(event: any) {
    const response: any = await this.searchService.search({ search: event.query, busqueda: 'actividades' });
    this.ratList = (response.data || []).map((item: { id_rat: number; nombre_rat: string }) => ({
      ...item,
      label: `${item.id_rat} - ${item.nombre_rat}`,
    }));
  }

  onRatSelected(event: any) {
    const selectedRat = event.value as RatSearchOption;
    const control = this.form.get('rat');
    this.ratSearch = selectedRat;
    control?.setValue(String(selectedRat.id_rat));
    control?.markAsDirty();
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  onRatSearchChange(value: RatSearchOption | string | null) {
    if (typeof value === 'string' || value === null) {
      const control = this.form.get('rat');
      control?.setValue('');
      control?.markAsDirty();
      control?.updateValueAndValidity();
    }
  }

  onRatBlur() {
    const control = this.form.get('rat');
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  onProcedureSelected(event: any) {
    this.currentProcedures.update((arr) => [...arr, event.value]);
    const control = this.form.get('selectedProcedure');
    control?.setValue(this.currentProcedures());
    setTimeout(() => this.clearProcedureSearch());
  }

  clearProcedureSearch() {
    this.procedureSearch = null;
    this.proceduresList = [];
    this.procedureSearchLoading = false;
  }
  removeProcedure(item: number) {
    const current = this.form.get('selectedProcedure')?.value || [];
    const updated = current.filter((i: any) => i.code !== item);
    const control = this.form.get('selectedProcedure');
    control?.setValue(updated);

    this.currentProcedures.update((arr: { code: number; name: string }[]) => arr.filter((i: any) => i.code !== item));
  }

  addApplication(event?: MouseEvent) {
    event?.preventDefault();
    const value = this.application();
    if (value) {
      this.applications.update((arr) => [...arr, value]);
      this.form.get('systemOrApp')?.setValue(this.applications());

      this.application.set('');
    }
  }

  removeApplication(item: string) {
    this.applications.update((apps) => apps.filter((a) => a !== item));
    this.form.get('systemOrApp')?.setValue(this.applications());
  }

  async onSave() {
    this.formSubmitted.set(true);
    if (this.form.invalid) {
      console.log('Form is invalid:', this.form);
      this.form.markAllAsTouched();
      return;
    }
    try {
      const rawFormData = this.form.getRawValue();
      const beforeRows = rawFormData.before as EmployeeRowValue[];
      const afterRows = rawFormData.after as EmployeeRowValue[];
      const formData = {
        ...rawFormData,
        totalPeopleBefore: beforeRows.map((row) => this.toNumber(row.totalPeople)),
        groupAdministrationBefore: beforeRows.map((row) => row.groupAdministration),
        levelPositionBefore: beforeRows.map((row) => this.toNumber(row.levelPosition)),
        totalPeopleAfter: afterRows.map((row) => this.toNumber(row.totalPeople)),
        groupAdministrationAfter: afterRows.map((row) => row.groupAdministration),
        levelPositionAfter: afterRows.map((row) => this.toNumber(row.levelPosition)),
        economicEstimateImplementation: this.optionalEconomicAmount(rawFormData.economicEstimateImplementation),
        economicEstimateAnnualMaintenance: this.optionalEconomicAmount(rawFormData.economicEstimateAnnualMaintenance),
        economicEstimateConsumption: this.optionalEconomicAmount(rawFormData.economicEstimateConsumption),
        otherDerivedCosts: this.optionalEconomicAmount(rawFormData.otherDerivedCosts),
      };
      delete formData.before;
      delete formData.after;
      delete formData.serviceName;

      const jobcode = this.form.get('jobcode')?.value;

      await this.projectsService.createNewProject(formData, this.userLogged.orgCode);

      this.form.reset();
      this.clearProcedureSearch();
      this.applications.set([]);
      this.form.get('jobcode')?.setValue(jobcode);

      this.router.navigate(['/catalogo-ia/proyectos/listado-proyectos']);
    } catch (error) {
      console.error('❌ Error al crear el proyecto:', error);
    }
  }

  backPage(): void {
    this.location.back();
  }
  confirm() {
    this.confirmationService.confirm({
      message: '¿Seguro que desea crear un proyecto.?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Guardar',
      },
      accept: () => {
        this.onSave();
      },
    });
  }
}
